import { gql } from '@apollo/client';
import { SvgLink, SvgPublicOff, SvgPublicOn } from '@loomhq/lens';
import React from 'react';

import { CREATE_PUBLIC_LINK } from 'constants/events';
import * as menuOptions from 'constants/menuOptions';
import { shareFolder, unshareFolder } from 'creators/videosDashboard';
import * as analytics from 'utilities/analytics';
import { getGraphQLClient } from 'utilities/graphql';
import {
  MenuOption,
  OnClickParameters,
  ShowParameters,
} from 'utilities/menuOptions/types';

const COPY_LINK: MenuOption = {
  key: menuOptions.COPY_LINK,
  label: menuOptions.COPY_LINK,
  icon: <SvgLink />,
  onClick: ({ dispatch, items }: OnClickParameters): void => {
    const item = items[0];

    if (item.__typename === 'RegularUserFolder') {
      dispatch(
        shareFolder(item.id, item.name, true, 'Destination Folder Action')
      );
    }
  },
  show: ({ items, currentUser, scopes }: ShowParameters) => {
    return (
      scopes.hasVideoSharePrivateScope &&
      scopes.hasAuthenticatedAccess &&
      items.length === 1 &&
      items.every(
        item =>
          item.__typename === 'RegularUserFolder' &&
          item.owner_id === currentUser.id &&
          !item.isArchived &&
          item.shared
      )
    );
  },
};
const MAKE_PRIVATE: MenuOption = {
  key: menuOptions.MAKE_PRIVATE,
  label: menuOptions.MAKE_PRIVATE,
  icon: <SvgPublicOff />,
  onClick: ({ dispatch, items }: OnClickParameters): void => {
    const item = items[0];

    if (item.__typename === 'RegularUserFolder') {
      dispatch(unshareFolder(item.id));

      // Optimistically modify the Apollo cache so the changes are reflected in the UI,
      // as the public folder actions use a REST endpoint to update the folder states
      const client = getGraphQLClient();
      const cacheId = client.cache.identify({
        id: item.id,
        __typename: 'RegularUserFolder',
      });

      client.cache.updateFragment(
        {
          id: cacheId,
          fragment: gql`
            fragment PublicFolder on RegularUserFolder {
              shared
            }
          `,
        },
        data => ({ ...data, shared: false })
      );
    }
  },
  show: ({ items, currentUser, scopes }: ShowParameters) => {
    return (
      scopes.hasVideoSharePrivateScope &&
      scopes.hasAuthenticatedAccess &&
      items.length === 1 &&
      items.every(
        item =>
          item.__typename === 'RegularUserFolder' &&
          item.owner_id === currentUser.id &&
          !item.isArchived &&
          item.shared
      )
    );
  },
};

const MAKE_PUBLIC: MenuOption = {
  key: menuOptions.MAKE_PUBLIC,
  label: menuOptions.MAKE_PUBLIC,
  icon: <SvgPublicOn />,
  onClick: ({ dispatch, items }: OnClickParameters): void => {
    const item = items[0];

    if (item.__typename === 'RegularUserFolder') {
      dispatch(shareFolder(item.id, item.name));
      analytics.track(CREATE_PUBLIC_LINK);

      // Optimistically modify the Apollo cache so the changes are reflected in the UI,
      // as the public folder actions use a REST endpoint to update the folder states
      const client = getGraphQLClient();
      const cacheId = client.cache.identify({
        id: item.id,
        __typename: 'RegularUserFolder',
      });

      client.cache.updateFragment(
        {
          id: cacheId,
          fragment: gql`
            fragment PublicFolder on RegularUserFolder {
              shared
            }
          `,
        },
        data => ({ ...data, shared: true })
      );
    }
  },
  show: ({ items, currentUser, scopes }: ShowParameters) => {
    return (
      scopes.hasVideoSharePrivateScope &&
      scopes.hasAuthenticatedAccess &&
      items.length === 1 &&
      items.every(
        item =>
          item.__typename === 'RegularUserFolder' &&
          item.owner_id === currentUser.id &&
          !item.isArchived &&
          !item.shared
      )
    );
  },
};

export default {
  COPY_LINK,
  MAKE_PRIVATE,
  MAKE_PUBLIC,
};
